/**
 * Hook de auto-logout por inactividad.
 *
 * Cumplimiento: GAMP 5 / 21 CFR Part 11 §11.10(d) — sesiones deben expirar
 * automáticamente cuando el usuario abandona la estación de trabajo (defensa
 * contra registros fraudulentos por un tercero).
 *
 * Comportamiento:
 *   1. Escucha eventos de input global (mousemove, keydown, click, scroll,
 *      touchstart) con throttle de 5s — actualiza `lastActivityAt`.
 *   2. Cada 30s revisa cuánto falta para el timeout.
 *   3. A `warningMinutos` antes, muestra toast warning con countdown.
 *   4. Al llegar a 0, audit + logout + redirect a /login con razón.
 *   5. Multi-tab: listener de storage events sincroniza lastActivityAt — si
 *      otra pestaña hace logout, esta también.
 *
 * Solo se monta cuando hay sesión activa (controlado desde AppShell).
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/notify';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import {
  usePoliticaSesionStore,
  msHastaTimeout,
} from '@/stores/politica-sesion';
import { refreshToken } from '@/api/endpoints/auth';
import { performLogout } from './logout-flow';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'click', 'scroll', 'touchstart'] as const;
const THROTTLE_MS = 5_000;
const CHECK_INTERVAL_MS = 30_000;
// Renovar el JWT cada 10 min mientras la sesión siga viva (no idle). El TTL
// del token es 30 min, así que esto lo desliza con margen y evita que expire
// mientras el usuario opera. El logout por inactividad real lo maneja el tick.
const REFRESH_EVERY_MS = 10 * 60_000;

export function useSessionTimeout(): void {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const usuario = useSessionStore((s) => s.usuario);
  const touchActivity = usePoliticaSesionStore((s) => s.touchActivity);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  // Refs para evitar re-suscripciones cuando cambian
  const ultimoTouchRef = useRef<number>(0);
  const warningMostradoRef = useRef<boolean>(false);
  const ultimoRefreshRef = useRef<number>(0);

  // 1) Inicializar lastActivityAt en mount (cuando hay usuario)
  useEffect(() => {
    if (!usuario) return;
    touchActivity();
    warningMostradoRef.current = false;
    // El login recién emitió un token fresco — arrancamos el contador de
    // refresh ahora para no renovar de más apenas montamos.
    ultimoRefreshRef.current = Date.now();
  }, [usuario, touchActivity]);

  // 2) Escuchar inputs del usuario con throttle
  useEffect(() => {
    if (!usuario) return;

    const handler = () => {
      const ahora = Date.now();
      if (ahora - ultimoTouchRef.current < THROTTLE_MS) return;
      ultimoTouchRef.current = ahora;
      touchActivity();
      // Si el usuario volvió a actuar, resetear flag de warning para que vuelva
      // a dispararse si pasa la próxima ventana sin actividad.
      warningMostradoRef.current = false;
    };

    for (const evento of ACTIVITY_EVENTS) {
      window.addEventListener(evento, handler, { passive: true });
    }
    return () => {
      for (const evento of ACTIVITY_EVENTS) {
        window.removeEventListener(evento, handler);
      }
    };
  }, [usuario, touchActivity]);

  // 3) Tick periódico: verificar timeout + disparar warning + auto-logout
  useEffect(() => {
    if (!usuario) return;

    const tick = () => {
      const { lastActivityAt, timeoutMinutos, warningMinutos } =
        usePoliticaSesionStore.getState();
      const restanteMs = msHastaTimeout(lastActivityAt, timeoutMinutos);

      if (restanteMs <= 0) {
        // EXPIRADO — auto-logout
        addAuditEntry({
          categoria: 'sesion',
          accion: 'sesion.timeout',
          razon: `Sesión expirada por inactividad (${timeoutMinutos} min)`,
        });
        toast.error('Sesión cerrada por inactividad', {
          description: `Pasaron ${timeoutMinutos} min sin actividad. Volvé a iniciar sesión.`,
          duration: 8000,
        });
        void performLogout({ navigate, queryClient, razon: 'timeout' });
        return;
      }

      // SLIDING SESSION — la sesión sigue viva (hubo actividad dentro de la
      // ventana): renovamos el JWT cada REFRESH_EVERY_MS para que no expire
      // mientras se opera. Si el refresh falla, el próximo 401 maneja el logout.
      const ahora = Date.now();
      if (ahora - ultimoRefreshRef.current >= REFRESH_EVERY_MS) {
        ultimoRefreshRef.current = ahora;
        void refreshToken().catch(() => {
          /* silencioso — un 401 posterior fuerza el logout normal */
        });
      }

      // WARNING — solo dispararlo una vez por ciclo
      const restanteMin = restanteMs / 60_000;
      if (restanteMin <= warningMinutos && !warningMostradoRef.current) {
        warningMostradoRef.current = true;
        const segs = Math.ceil(restanteMs / 1000);
        toast.warning(`Sesión próxima a expirar`, {
          description: `Faltan ${segs}s antes del cierre automático por inactividad. Cualquier acción reanuda el contador.`,
          duration: Math.min(restanteMs, 15_000),
        });
      }
    };

    // Tick inmediato + intervalo
    tick();
    const id = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [usuario, addAuditEntry, navigate, queryClient]);
}
